import { useEffect, useMemo, useState } from "react";
import { FolderSync, Download, Search, X, Phone, MessageCircle, RefreshCw, IndianRupee, CheckCircle2, ClipboardList, Landmark, Building2, MapPin, User as UserIcon, ExternalLink, MessagesSquare, Copy, Link as LinkIcon, UserPlus, AlertTriangle, ArrowRight, Clock } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { useRefocus } from "@/lib/refocus";
import LoadingSkeleton from "@/components/app/LoadingSkeleton";
import Paginator from "@/components/app/Paginator";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const initials = (name) => (name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch { return "—"; }
}

// -------------------------------------------------------------------------
// Profile drawer (sliding sheet from the right)
// -------------------------------------------------------------------------
function ProgressStep({ label, done, active, Icon }) {
  return (
    <div className={`flex items-center gap-2 py-1.5 ${active ? "text-orange-400" : done ? "text-emerald-400" : "text-slate-600"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${active ? "bg-orange-500/15 border-orange-500 animate-pulse" : done ? "bg-emerald-500/15 border-emerald-500" : "bg-slate-800 border-slate-700"}`}>
        <Icon className="w-3.5 h-3.5"/>
      </div>
      <span className={`text-xs uppercase tracking-widest font-bold ${active ? "" : "opacity-80"}`}>{label}</span>
    </div>
  );
}

function ApprovalDot({ label, status }) {
  const cls = status === "approved" ? "text-emerald-400" : status === "rejected" ? "text-red-400" : "text-slate-500";
  const sym = status === "approved" ? "✓" : status === "rejected" ? "✗" : "…";
  return <span className={`inline-flex items-center gap-1 text-[11px] ${cls}`}><b>{label}</b> {sym}</span>;
}

function ProfileDrawer({ leadId, open, onClose, onChange, handoverFocus = false }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [handoverStaffId, setHandoverStaffId] = useState("");
  const [replacing, setReplacing] = useState(false);
  const [bankStaffPool, setBankStaffPool] = useState([]);

  useEffect(() => {
    if (!open || !leadId) return;
    setLoading(true); setDetail(null); setHandoverStaffId("");
    api.get(`/admin/moving-files/${leadId}`)
      .then(r => {
        setDetail(r.data);
        // If admin opened from the "Awaiting Handover" strip, scroll the handover
        // section into view + show a nudge toast so the flow is obvious.
        if (handoverFocus) {
          setTimeout(() => {
            const el = document.querySelector('[data-testid="drawer-wa-group"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            toast.info("Case returned — pick a new bank staff to complete the handover");
          }, 250);
        }
      })
      .catch(e => toast.error(errorMessage(e)))
      .finally(() => setLoading(false));
    api.get("/admin/bank-staff").then(r => setBankStaffPool(r.data || [])).catch(() => setBankStaffPool([]));
  }, [open, leadId, handoverFocus]);

  const step = detail?.progress_step || 2;
  const hasGroup = !!detail?.wa_group_invite_link;

  const runReplaceStaff = async () => {
    if (!handoverStaffId) return toast.error("Pick a new bank staff first");
    setReplacing(true);
    try {
      const r = await api.post(`/admin/case/${leadId}/wa-group/replace-staff`, { new_bank_staff_id: handoverStaffId });
      try { await navigator.clipboard.writeText(r.data.onboarding_message); } catch {}
      window.open(r.data.onboarding_wa_url, "_blank", "noopener,noreferrer");
      // Clear the awaits-handover flag so the row disappears from the strip
      try { await api.post(`/admin/case/${leadId}/wa-group/clear-handover`); } catch {}
      toast.success(`Handover brief copied · Remove ${r.data.old_bank_staff?.name || "old staff"} · Add ${r.data.new_bank_staff?.name}`);
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setReplacing(false); }
  };

  const copyInvite = async () => {
    if (!detail?.wa_group_invite_link) return;
    try { await navigator.clipboard.writeText(detail.wa_group_invite_link); toast.success("Invite link copied"); }
    catch { toast.error("Copy failed — long-press to select"); }
  };

  const [markingCommission, setMarkingCommission] = useState(false);
  const markCommission = async (paid) => {
    setMarkingCommission(true);
    try {
      const url = paid ? `/admin/leads/${leadId}/commission-paid` : `/admin/leads/${leadId}/commission-pending`;
      await api.post(url);
      toast.success(paid ? "Commission marked PAID" : "Commission marked PENDING");
      const d = await api.get(`/admin/moving-files/${leadId}`);
      setDetail(d.data);
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setMarkingCommission(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-[#0A0E1E] border-l border-[#1E2742] text-white p-0 !max-w-[420px] w-full sm:w-[420px] fixed !right-0 !left-auto top-0 h-screen !translate-x-0 !translate-y-0 rounded-none overflow-y-auto"
        data-testid="moving-profile-drawer"
      >
        <DialogHeader className="p-4 border-b border-[#1E2742] sticky top-0 bg-[#0A0E1E] z-10">
          <DialogTitle className="flex items-center gap-2 text-white">
            <UserIcon className="w-4 h-4 text-[#C5A028]"/> Customer Profile
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="text-center text-slate-500 py-8">Loading…</div>}
        {detail && (
          <div className="p-4 space-y-4">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-[#C5A028] to-[#8a6f1c] flex items-center justify-center text-slate-900 font-bold text-xl">
                {initials(detail.customer_name)}
              </div>
              <div className="mt-2 font-heading text-xl text-white" data-testid="drawer-customer-name">{detail.customer_name}</div>
              <div className="font-mono-alt text-xs text-[#C5A028] mt-0.5">{detail.lead_id}</div>
              <div className="mt-2 flex justify-center gap-2 flex-wrap">
                {detail.customer_phone && (
                  <a href={`tel:${detail.customer_phone}`} data-testid="drawer-call-customer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 text-slate-900 text-xs font-bold">
                    <Phone className="w-3 h-3"/>{detail.customer_phone}
                  </a>
                )}
                {detail.customer_phone && (
                  <a href={`https://wa.me/91${detail.customer_phone}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#25D366] text-white text-xs font-bold">
                    <MessageCircle className="w-3 h-3"/>WhatsApp
                  </a>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-2.5">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Area</div>
                <div className="text-white font-semibold mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-500"/>{detail.district || "—"}</div>
              </div>
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-2.5">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Loan Type</div>
                <div className="text-white font-semibold mt-0.5">{detail.loan_type || "—"}</div>
              </div>
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-2.5">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Loan Amount</div>
                <div className="text-white font-bold mt-0.5 font-mono-alt">₹{(detail.amount || 0).toLocaleString("en-IN")}</div>
              </div>
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-2.5">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Commission ({detail.commission_pct}%)</div>
                <div className="text-emerald-400 font-bold mt-0.5 font-mono-alt">₹{(detail.commission_amount || 0).toLocaleString("en-IN")}</div>
              </div>
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-2.5">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Bank / Finance</div>
                <div className="text-white font-semibold mt-0.5">{detail.bank_name || "—"}</div>
              </div>
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-2.5">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Lead Date</div>
                <div className="text-white font-semibold mt-0.5">{fmtDate(detail.lead_date)}</div>
              </div>
            </div>

            {/* WhatsApp Group — Admin is READ-ONLY. Only the assigned telecaller creates/edits. */}
            <div className="rounded-lg bg-[#141A2E] border border-[#25D366]/25 p-3" data-testid="drawer-wa-group">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center">
                  <MessagesSquare className="w-4 h-4 text-[#25D366]"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#25D366] font-bold">WhatsApp Group · Read-only</div>
                  <div className="text-[10px] text-slate-500">Created by the assigned telecaller</div>
                </div>
              </div>
              {detail.wa_group_name && (
                <div className="text-[11px] text-slate-300 font-mono-alt border border-slate-800 bg-[#0F1424] rounded-md px-2 py-1 mb-2 break-all" data-testid="drawer-wa-name">
                  {detail.wa_group_name}
                </div>
              )}
              <div className="text-[10px] text-slate-500 mb-2" data-testid="drawer-wa-status">
                {hasGroup
                  ? <>Created by <b className="text-emerald-400">{detail.wa_group_created_by_name || "Telecaller"}</b>{detail.wa_group_created_by_role ? ` (${(detail.wa_group_created_by_role||"").replace("_"," ")})` : ""}{detail.wa_group_created_at ? ` · ${new Date(detail.wa_group_created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</>
                  : <span className="text-orange-400">Not yet created by telecaller</span>}
              </div>
              {hasGroup ? (
                <>
                  <div className="rounded-md bg-[#0F1424] border border-slate-800 px-2 py-1.5 text-[11px] font-mono-alt text-slate-300 break-all mb-2" data-testid="drawer-wa-link">
                    {detail.wa_group_invite_link}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <a
                      href={detail.wa_group_invite_link}
                      target="_blank" rel="noreferrer"
                      data-testid="wa-join-group"
                      className="h-9 rounded-md bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold flex items-center justify-center gap-1"
                    ><LinkIcon className="w-3 h-3"/>Open Group</a>
                    <button type="button" onClick={copyInvite} data-testid="wa-copy-invite"
                      className="h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-xs font-bold flex items-center justify-center gap-1">
                      <Copy className="w-3 h-3"/>Copy Link
                    </button>
                  </div>
                </>
              ) : (
                <div className="rounded-md bg-orange-500/10 border border-orange-500/30 px-2.5 py-2 text-[11px] text-orange-300 flex items-center gap-2" data-testid="drawer-wa-empty">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0"/> Waiting for the telecaller to create this WA group.
                </div>
              )}

              {/* Replace bank staff */}
              {detail.bank_staff && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1.5">Handover to new Bank Staff</div>
                  <div className="flex gap-1.5">
                    <select
                      data-testid="wa-handover-select"
                      value={handoverStaffId}
                      onChange={e => setHandoverStaffId(e.target.value)}
                      className="flex-1 h-9 rounded-md bg-[#0F1424] border border-slate-700 text-white text-xs px-2"
                    >
                      <option value="">Pick new bank staff…</option>
                      {bankStaffPool
                        .filter(s => s.id !== detail.bank_staff.id && s.status !== "terminated")
                        .map(s => <option key={s.id} value={s.id}>{s.name} · {s.bank_name || "—"} · {s.district || "—"}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={runReplaceStaff}
                      disabled={!handoverStaffId || replacing}
                      data-testid="wa-handover-btn"
                      className="shrink-0 px-3 h-9 rounded-md bg-orange-500 hover:bg-orange-400 text-slate-900 text-xs font-bold disabled:opacity-40 flex items-center gap-1"
                    ><UserPlus className="w-3 h-3"/>Handover</button>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
                    Generates a handover brief with full case context (approvals, rejections, contacts) so new staff joins productively — no chat scrollback needed.
                  </div>
                </div>
              )}
            </div>

            {/* Progress */}
            <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Progress</div>
              <ProgressStep label="Assigned"  Icon={ClipboardList} done={step > 1} active={step === 1}/>
              <ProgressStep label="Bank Pool" Icon={Landmark}      done={step > 2} active={step === 2}/>
              <ProgressStep label="Moving"    Icon={FolderSync}    done={step > 3} active={step === 3}/>
              <ProgressStep label="Disbursed" Icon={CheckCircle2}  done={step > 4} active={step === 4}/>
              <div className="flex gap-3 mt-2 pt-2 border-t border-slate-800">
                <ApprovalDot label="CIBIL" status={detail.cibil_status}/>
                <ApprovalDot label="Legal" status={detail.legal_status}/>
                <ApprovalDot label="Tech"  status={detail.tech_status}/>
              </div>
            </div>

            {/* Telecaller + Bank Staff */}
            {detail.telecaller && (
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-3 flex items-center gap-2" data-testid="drawer-telecaller">
                <div className="w-9 h-9 rounded-full bg-blue-500/25 border border-blue-500/40 text-blue-300 flex items-center justify-center text-xs font-bold">{initials(detail.telecaller.name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">Telecaller</div>
                  <div className="text-white font-semibold truncate">{detail.telecaller.name}</div>
                </div>
                {detail.telecaller.phone && <a href={`tel:${detail.telecaller.phone}`} className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-300"><Phone className="w-3.5 h-3.5"/></a>}
              </div>
            )}
            {detail.bank_staff && (
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-3 flex items-center gap-2" data-testid="drawer-bank-staff">
                <div className="w-9 h-9 rounded-full bg-[#C5A028]/25 border border-[#C5A028]/40 text-[#C5A028] flex items-center justify-center text-xs font-bold">{initials(detail.bank_staff.name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">Bank Staff</div>
                  <div className="text-white font-semibold truncate">{detail.bank_staff.name} <span className="text-slate-400 text-xs font-normal">· {detail.bank_staff.bank_name || detail.bank_name || ""}</span></div>
                </div>
                {detail.bank_staff.phone && <a href={`tel:${detail.bank_staff.phone}`} className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-300"><Phone className="w-3.5 h-3.5"/></a>}
              </div>
            )}

            {detail.notes && (
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Notes</div>
                <div className="text-slate-200 text-sm whitespace-pre-wrap">{detail.notes}</div>
              </div>
            )}

            {/* Referral Commission — 1% payable to referring bank staff */}
            {detail.is_referral && (
              <div
                data-testid="drawer-referral-commission"
                className="rounded-lg border-2 p-3"
                style={{
                  background: "linear-gradient(140deg, #1a1305 0%, #2b1f08 100%)",
                  borderColor: "rgba(212,175,55,0.5)",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/25 border border-[#D4AF37]/50 text-[#D4AF37] flex items-center justify-center text-sm font-bold">1%</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Referral Commission</div>
                    <div className="text-xs text-slate-400 truncate">Referred by <span className="text-white font-semibold">{detail.referred_by_staff_name || "—"}</span> · {detail.referred_by_staff_role === "bank_staff" ? "Bank Staff" : (detail.referred_by_staff_role || "").replace(/_/g," ")}</div>
                  </div>
                </div>
                <div className="rounded-md bg-black/30 border border-[#D4AF37]/30 p-2 mb-2">
                  <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]/70">Amount Payable</div>
                  <div className="text-[#F4CF52] font-mono-alt font-black text-lg">₹{(detail.referral_commission_amount || 0).toLocaleString("en-IN")}</div>
                </div>
                <div className="text-[10px] mb-2">
                  Status: {detail.commission_paid ? (
                    <span className="text-emerald-400 font-bold uppercase tracking-widest">Paid ✓ {detail.commission_paid_at ? "on " + fmtDate(detail.commission_paid_at) : ""}</span>
                  ) : (
                    <span className="text-[#F97316] font-bold uppercase tracking-widest">Pending</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={markingCommission || detail.commission_paid}
                    onClick={() => markCommission(true)}
                    data-testid="mark-commission-paid"
                    className="h-10 rounded-full bg-[#22C55E] hover:bg-[#16a34a] text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                  >Mark Commission Paid</button>
                  <button
                    type="button"
                    disabled={markingCommission || !detail.commission_paid}
                    onClick={() => markCommission(false)}
                    data-testid="mark-commission-pending"
                    className="h-10 rounded-full bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                  >Mark Pending</button>
                </div>
              </div>
            )}

            {detail.history?.length > 0 && (
              <div className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Call History / Timeline</div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {detail.history.slice(-20).reverse().map((h, i) => (
                    <div key={i} className="text-[11px] text-slate-300 border-l-2 border-slate-700 pl-2 py-0.5">
                      <span className="text-slate-500 font-mono-alt mr-1">{fmtDate(h.at)}</span> {h.event}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------------
export default function AdminMovingFiles() {
  const [rows, setRows] = useState([]);
  const [awaitingHandover, setAwaitingHandover] = useState([]);
  const [totals, setTotals] = useState({ loan_amount: 0, commission_amount: 0, net_amount: 0, count: 0, unhealthy: 0, awaiting_handover: 0 });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [drawerLead, setDrawerLead] = useState(null);
  const [handoverFocus, setHandoverFocus] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPage(1); }, [debouncedQ]);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    if (debouncedQ) params.set("q", debouncedQ);
    api.get(`/admin/moving-files?${params.toString()}`)
      .then(r => {
        setRows(r.data.rows || []);
        setAwaitingHandover(r.data.awaiting_handover || []);
        setTotals(r.data.totals || {});
        setTotal(r.data.total || 0);
        setLoaded(true);
      })
      .catch(e => toast.error(errorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [page, debouncedQ]);
  useRefocus(load);

  const openHandover = (leadId) => {
    setHandoverFocus(true);
    setDrawerLead(leadId);
  };
  const closeDrawer = () => {
    setDrawerLead(null);
    setHandoverFocus(false);
  };

  const filtered = useMemo(() => rows, [rows]);

  const exportCSV = () => {
    const header = ["S.N", "Customer Name", "Phone (hidden in view)", "Telecaller", "Bank / Finance", "Bank Staff", "Loan Amount", "Commission (2%)", "Lead Date"];
    const csv = [header.join(",")].concat(filtered.map(r => [
      r.sn, r.customer_name, "", r.telecaller_name, r.bank_name, r.bank_staff_name,
      r.loan_amount, r.commission_amount, r.lead_date,
    ].map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `moving-files-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card-surface p-6" data-testid="admin-moving-files">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
            <FolderSync className="w-5 h-5 text-orange-400"/>
          </div>
          <div>
            <div className="font-heading text-2xl text-white">Moving Files</div>
            <div className="text-xs text-slate-500 mt-0.5">Excel view · every file a bank staff has picked up but not yet disbursed.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500"/>
            <Input
              data-testid="moving-search"
              placeholder="Search customer, staff, bank…"
              value={q}
              onChange={e => setQ(e.target.value)}
              className="pl-8 bg-slate-950 border-slate-700 w-64 h-9"
            />
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="moving-refresh"
            className="w-9 h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400"
          ><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/></button>
          <Button
            data-testid="moving-export"
            onClick={exportCSV}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-md h-9"
          ><Download className="w-4 h-4 mr-1"/>Export</Button>
        </div>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl bg-[#141A2E] border border-[#1E2742] p-4" data-testid="stat-loan">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Loan Amount</div>
          <div className="font-display text-2xl text-white mt-1 font-mono-alt">₹{(totals.loan_amount || 0).toLocaleString("en-IN")}</div>
        </div>
        <div className="rounded-xl bg-[#141A2E] border border-emerald-500/25 p-4" data-testid="stat-commission">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Total Commission (2%)</div>
          <div className="font-display text-2xl text-emerald-400 mt-1 font-mono-alt">₹{(totals.commission_amount || 0).toLocaleString("en-IN")}</div>
        </div>
        <div className="rounded-xl bg-[#141A2E] border border-[#C5A028]/25 p-4" data-testid="stat-count">
          <div className="text-[10px] uppercase tracking-widest text-[#C5A028]">Files In Motion</div>
          <div className="font-display text-2xl text-white mt-1 font-mono-alt">{totals.count || 0}</div>
        </div>
      </div>

      {/* Awaiting Handover strip — cases where BS hit Return but the WA group survives */}
      {awaitingHandover.length > 0 && (
        <div className="rounded-xl border border-orange-500/40 bg-orange-500/5 p-3 mb-4" data-testid="awaiting-handover-strip">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-orange-400"/>
            <div className="text-[11px] uppercase tracking-widest text-orange-400 font-bold">
              Awaiting Handover · {awaitingHandover.length}
            </div>
            <div className="text-xs text-slate-400">Bank staff returned these — WhatsApp group is still active, pick a new staff to hand over.</div>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {awaitingHandover.map(h => (
              <button
                type="button"
                key={h.lead_id}
                onClick={() => openHandover(h.lead_id)}
                data-testid={`handover-row-${h.lead_id}`}
                className="text-left rounded-lg bg-[#141A2E] border border-orange-500/30 hover:border-orange-500 p-3 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono-alt text-[10px] text-[#C5A028]">{h.lead_id}</div>
                    <div className="text-white font-bold truncate">{h.customer_name}</div>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">
                      Returned by <span className="text-red-300">{h.prior_bank_staff_name}</span> · {h.return_reason || "no reason"}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 font-mono-alt">₹{(h.loan_amount || 0).toLocaleString("en-IN")} · {h.loan_type}</div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1 text-orange-400 text-xs font-bold">
                    Handover<ArrowRight className="w-3 h-3"/>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-[#1E2742] overflow-x-auto bg-[#141A2E]">
        <table className="w-full text-sm min-w-[820px]" data-testid="moving-table">
          <thead>
            <tr className="bg-[#0F1424] text-[#C5A028] uppercase tracking-widest text-[10px]">
              <th className="text-left px-3 py-2.5 w-12">S.N</th>
              <th className="text-left px-3 py-2.5">Customer Name</th>
              <th className="text-left px-3 py-2.5">Telecaller</th>
              <th className="text-left px-3 py-2.5">Bank / Finance</th>
              <th className="text-left px-3 py-2.5">Bank Staff</th>
              <th className="text-right px-3 py-2.5">Loan Amount</th>
              <th className="text-right px-3 py-2.5">Commission (2%)</th>
              <th className="text-left px-3 py-2.5">Lead Date</th>
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-8" data-testid="moving-loading">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-8" data-testid="moving-empty">No files currently in motion.</td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.lead_id} className="border-t border-[#1E2742] hover:bg-[#1E2742]/40 transition-colors" data-testid={`moving-row-${r.lead_id}`}>
                <td className="px-3 py-2.5 font-mono-alt text-slate-500">{r.sn}</td>
                <td className="px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => setDrawerLead(r.lead_id)}
                    data-testid={`moving-name-${r.lead_id}`}
                    className="text-white font-bold text-left hover:text-blue-400 hover:underline decoration-blue-400 cursor-pointer flex items-center gap-1"
                  >
                    {r.customer_name}
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100"/>
                  </button>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <div className="text-[9px] font-mono-alt text-[#C5A028]/70">{r.lead_id}</div>
                    {r.awaiting_take && (
                      <Badge
                        data-testid={`awaiting-take-${r.lead_id}`}
                        className="bg-amber-500/20 text-amber-300 border border-amber-500/50 text-[8px] uppercase tracking-widest px-1.5 py-0 animate-pulse whitespace-nowrap"
                        title="Admin has assigned this case but the bank staff hasn't clicked Take yet — nudge them.">
                        ⏳ Waiting Bank Accept
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <span className="w-6 h-6 rounded-full bg-blue-500/25 border border-blue-500/40 flex items-center justify-center text-[9px] font-bold text-blue-300">{initials(r.telecaller_name)}</span>
                    <span className="text-slate-200 text-xs">{r.telecaller_name}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-[10px]">{r.bank_name}</Badge>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <span className="relative w-6 h-6 rounded-full bg-[#C5A028]/25 border border-[#C5A028]/40 flex items-center justify-center text-[9px] font-bold text-[#C5A028]">
                      {initials(r.bank_staff_name)}
                      {r.status === "taken" && <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 border border-[#141A2E]"/>}
                    </span>
                    <span className="text-slate-200 text-xs">{r.bank_staff_name}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right font-mono-alt text-white font-bold">₹{(r.loan_amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5 text-right font-mono-alt text-emerald-400 font-bold">₹{(r.commission_amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span>{fmtDate(r.lead_date)}</span>
                    {r.wa_group_invite_link ? (
                      <a
                        href={r.wa_group_invite_link}
                        target="_blank" rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        data-testid={`wa-cell-${r.lead_id}`}
                        title="Open WhatsApp Group"
                        className="w-6 h-6 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 flex items-center justify-center text-[#25D366]"
                      ><MessagesSquare className="w-3 h-3"/></a>
                    ) : (
                      <div className="relative" title={r.wa_health === "unhealthy" ? `${r.hours_since_taken}h without WA group — awaiting telecaller` : "Awaiting telecaller to create WA group"}>
                        <div
                          data-testid={`wa-waiting-${r.lead_id}`}
                          className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                            r.wa_health === "unhealthy"
                              ? "bg-orange-500/15 border-orange-500/50 text-orange-300 animate-pulse"
                              : "bg-slate-800 border-slate-700 text-slate-500"
                          }`}
                        ><Clock className="w-3 h-3"/></div>
                        {r.wa_health === "unhealthy" && (
                          <span
                            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-400 border border-[#141A2E] animate-pulse"
                            data-testid={`wa-health-dot-${r.lead_id}`}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProfileDrawer leadId={drawerLead} open={!!drawerLead} onClose={closeDrawer} onChange={load} handoverFocus={handoverFocus}/>

      {loaded && total > 0 && (
        <Paginator page={page} pageSize={pageSize} total={total} onPage={setPage} testid="moving-pager"/>
      )}
    </div>
  );
}
